import React from "react";
import { Search, Users, ShieldCheck, Clock3, TrendingDown, Store } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";

const BENEFITS = [
    {
        icon: Search,
        title: "Finding the missing",
        body: "Recognition of lawful-access images can locate missing people — including vulnerable adults and children — far faster than manual review of hours of footage.",
    },
    {
        icon: ShieldCheck,
        title: "Solving serious crime",
        body: "Retrospective search of images police are already entitled to hold can connect suspects to serious offences — robbery, assault, abduction — that eyewitnesses alone cannot resolve.",
    },
    {
        icon: Clock3,
        title: "Speed under pressure",
        body: "Automated triage of footage compresses days of human review into minutes, letting officers act while a lead is still live. In the 2020 Clearview trial, Police's concern was not that the concept lacked utility — it was that the governance and accuracy were not there (Independent Police Conduct Authority, 2020).",
    },
    {
        icon: TrendingDown,
        title: "Consistency at scale",
        body: "Software does not fatigue. Where a human reviewer's accuracy degrades across a twelve-hour shift, an automated system applies the same threshold to frame one and frame one million.",
    },
    {
        icon: Store,
        title: "Protecting frontline workers",
        body: "The strongest recent NZ evidence base is retail: Foodstuffs' trial targeted repeat serious offenders assaulting or threatening staff, with verified matches only and watchlists limited to individual stores (Office of the Privacy Commissioner, 2025).",
    },
    {
        icon: Users,
        title: "Consented verification",
        body: "FRT used with consent — unlocking a phone, a passport e-gate — delivers most of the technology's convenience with a fraction of the rights impact, because the subject knows and agrees.",
    },
];

export default function Benefits() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-04"
                lines={["Benefits &", <>opportunities — <i className="text-amber">the case for</i></>]}
                lede="An honest dossier names what is genuinely at stake in restricting this technology. Used narrowly, retrospectively and with verified human confirmation, FRT offers real public-safety value. The position we test on this page is the technology's best case — the pages that follow test it hard."
                meta={["SUBJECT: UPSIDE ANALYSIS"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="4.1"
                        kicker="The case for"
                        title="Six claims the evidence supports — with conditions"
                        lede="Each benefit is real, and each is conditional on accuracy, proportionality and oversight. Where those conditions fail, so does the benefit."
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {BENEFITS.map((b, i) => (
                            <Reveal key={b.title} delay={i * 0.06} className="bg-ink">
                                <div className="p-7 sm:p-8 h-full" data-testid={`benefit-card-${i + 1}`}>
                                    <b.icon size={20} className="text-amber mb-6" />
                                    <h3 className="font-display text-xl text-paper mb-3">{b.title}</h3>
                                    <p className="text-sm leading-relaxed text-ash">{b.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
                    <SectionHead no="4.2" kicker="Reading the evidence" title="What the Foodstuffs inquiry actually endorsed" />
                    <div className="grid lg:grid-cols-3 gap-8">
                        <Reveal className="lg:col-span-2">
                            <p className="text-base sm:text-lg leading-relaxed text-ash mb-6">
                                The Privacy Commissioner's 2025 report is the closest thing New Zealand has to a
                                judicial weighing of FRT's costs and benefits — and it is more instructive than a
                                simple approval. The inquiry found the supermarket trial <span className="text-paper">justified and compliant</span>:
                                99.999% of the 225.97 million scanned faces were deleted within a minute, matches
                                required verification by two trained staff, and watchlists could not follow a person
                                from store to store (Office of the Privacy Commissioner, 2025).
                            </p>
                            <p className="text-base sm:text-lg leading-relaxed text-ash">
                                But the Commissioner refused to call it a green light. Two misidentifications forced
                                the confidence threshold up from 90% to 92.5% before staff could intervene, and the
                                report warned the technology was trained on overseas datasets — leaving unresolved
                                potential for bias against Māori and Pacific people. The ruling phrase: FRT demands
                                <span className="text-amber"> “caution, strong justification, and rigorous system design”</span> —
                                an amber light (Office of the Privacy Commissioner, 2025).
                            </p>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="border border-amber/30 bg-amber/[0.05] p-7 h-full flex flex-col justify-between gap-8">
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-4">The condition set</p>
                                    <ul className="space-y-3 text-sm text-paper/85 leading-relaxed">
                                        <li>— Verified by trained humans before action</li>
                                        <li>— High confidence thresholds, raised after review</li>
                                        <li>— Immediate deletion of non-matches</li>
                                        <li>— Narrow, purpose-bound watchlists</li>
                                        <li>— Accuracy tested for NZ demographics</li>
                                    </ul>
                                </div>
                                <p className="font-mono text-[11px] leading-relaxed text-ash">
                                    Strip any one of these away and the benefit case collapses with it.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
